import type {
  ConsumptionMeasurementBasis,
  ConsumptionQuality,
  ConsumptionSelectionBlockedReason,
  ConsumptionSourceSystem,
} from "~/api/dto/proposal-consumption.dto"

/**
 * Seller-facing names for the safe consumption domain values. Every Proposal
 * Setup surface renders source metadata through these maps so provider
 * details never leak and the vocabulary stays consistent.
 */
export const SOURCE_LABELS: Record<ConsumptionSourceSystem, string> = {
  ARCADIA_PLUG: `Green Button`,
  AURORA: `Aurora`,
  VISION: `Vision`,
}

export const QUALITY_LABELS: Record<ConsumptionQuality, string> = {
  MEASURED: `Measured`,
  MODELED: `Modeled`,
  PARTIAL: `Partial`,
  UNKNOWN: `Unknown`,
  USER_ENTERED: `Seller entered`,
}

export const MEASUREMENT_BASIS_LABELS: Record<
  ConsumptionMeasurementBasis,
  string
> = {
  GRID_IMPORT: `Grid import`,
  MODELED_NATIVE_LOAD: `Modeled household load`,
  NATIVE_LOAD: `Household load`,
}

export const BLOCKED_REASON_COPY: Record<
  ConsumptionSelectionBlockedReason,
  string
> = {
  grid_import_basis: `This meter reports grid import from an existing solar system, so it cannot stand in for household usage.`,
  incoherent_summaries: `This profile's annual and monthly totals disagree, so it cannot be selected.`,
  not_measured: `This profile is not a complete measured year of usage.`,
}

export const LOCKED_CONSUMPTION_RESUME_COPY = `Manual edits lock this choice. Selecting Green Button data resumes provider-managed usage.`

export const LOCKED_HOUSEHOLD_CONSUMPTION_COPY = `Manual edits lock this household consumption choice. They do not change which data Battery savings uses.`

export const GRID_IMPORT_CONNECTED_COPY = `This Green Button connection is available to review. It reports grid import from an existing solar system, so it cannot replace this proposal's household consumption.`

const MODELING_METHOD_LABELS: Record<string, string> = {
  scale_to_365: `observed usage scaled to a full year`,
  seasonal_monthly_extrapolation: `observed seasonal usage extrapolated to a full year`,
}

export const modelingMethodLabel = (modelingMethod: string | null): string =>
  (modelingMethod && MODELING_METHOD_LABELS[modelingMethod]) ??
  `modeled from observed usage`

export const formatCoveragePercent = (
  coveragePercent: number | null,
): string | null =>
  coveragePercent === null
    ? null
    : `${Math.round(coveragePercent)}% of a year covered`
